import { HttpClient } from '@angular/common/http';
import { DestroyRef, Injectable, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { EntityConfigField, EntityConfigFieldInput, MappedEntityConfigField } from '@innovation-os-ng/core/shared';
import { SpaceSelectors, skipper } from '@innovation-os-ng/core/state';
import { EnvironmentService } from '@innovation-os-ng/core/utils';
import { Store } from '@ngrx/store';
import { Observable, ReplaySubject, catchError, lastValueFrom, map, of, take, tap, throwError } from 'rxjs';
import { ITONICS_URIS } from '../constants/itonicsUri';
import { ErrorUIType, ErrorUiService } from './error-ui.service';
@Injectable({
  providedIn: 'root'
})
export class EntityConfigFieldService {
  private readonly destroyRef = inject(DestroyRef);
  private readonly GET_API_URL: string = this.environmentService.getRestUrl('/elements-config/fields/space/');
  private readonly POST_API_URL: string = this.environmentService.getRestUrl('/elements-config/fields');
  /**
   * Holds field values
   */
  private spaceFields: {
    [spaceUri: string]: ReplaySubject<Array<EntityConfigField>>;
  } = {};

  private spaceMappedFields: {
    [spaceUri: string]: MappedEntityConfigField;
  } = {};

  private activeSpaceUri: string;

  constructor(
    private http: HttpClient,
    private readonly environmentService: EnvironmentService,
    private readonly errorUiService: ErrorUiService,
    private readonly store: Store
  ) {
    this.store
      .select(SpaceSelectors.selectCurrentSpaceUri)
      .pipe(skipper(), takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (currentSpaceUri) => {
          this.activeSpaceUri = currentSpaceUri;
        }
      });
  }

  /**
   * Save field data
   *
   * @param field
   * @returns recently saved field data (Observable<EntityConfigField>)
   */
  public saveField(field: EntityConfigFieldInput, spaceUri: string): Observable<EntityConfigField> {
    return this.http.post(this.POST_API_URL, field).pipe(
      catchError((error) => {
        this.errorUiService.notifyError({
          type: ErrorUIType.REQUEST,
          value: error,
          element: field
        });
        return throwError(() => error);
      }),
      map(([savedField]: Array<EntityConfigField>) => savedField),
      tap(async (savedField: EntityConfigField) => {
        const cachedFields: Array<EntityConfigField> = await lastValueFrom(this.spaceFields[spaceUri].pipe(take(1)));

        if (this.spaceFields[spaceUri]) {
          this.spaceFields[spaceUri].next([...cachedFields, savedField]);
        }
      })
    );
  }

  /**
   * Get space fields
   *
   * @returns recently saved field data (Observable<EntityConfigField>)
   */
  private loadFields(spaceUri: string) {
    if (!this.spaceFields[spaceUri]) {
      this.spaceFields[spaceUri] = new ReplaySubject<Array<EntityConfigField>>(1);
    }
    const url: string = this.GET_API_URL + spaceUri;
    this.http
      .get(url)
      // Assign default value to identify default type in new EC, this is the temporary approach and will be removed once the API sends the value
      .pipe(map((fields: Array<EntityConfigField>) => fields.map((field) => ({ ...field, default: this.hasItonicsSpaceUri(field) }))))
      .subscribe({
        next: (fields: Array<EntityConfigField>) => {
          if (!fields?.length) {
            this.spaceFields[spaceUri].next([]);
            return;
          }
          this.spaceFields[spaceUri].next(fields);

          // Update space controls map data
          this.spaceMappedFields[spaceUri] = fields.reduce(
            (mappedFields: MappedEntityConfigField, field: EntityConfigField) => ({ ...mappedFields, [field.uri]: field }),
            {}
          );
        },
        error: (err) => {
          this.errorUiService.notifyError({
            type: ErrorUIType.REQUEST,
            value: err,
            element: null
          });

          this.spaceFields[spaceUri].next([]);
        }
      });
  }

  /**
   * @param spaceUri
   * @param freshFetch - Won't check cache existence and force fresh fetch field
   */
  public getFields(spaceUri: string, freshFetch: boolean = false) {
    if (!this.spaceFields[spaceUri] || freshFetch) {
      this.loadFields(spaceUri);
    }
    return this.spaceFields[spaceUri].asObservable();
  }

  /**
   * Checks for mapped fields information in the cache and provides it.
   */
  public getMappedFields(): MappedEntityConfigField {
    console.assert(!!this.activeSpaceUri, 'activeSpaceUri should not be null');
    return this.spaceMappedFields[this.activeSpaceUri];
  }

  /**
   * Provides field information of provided space uri check in the cache first if
   * not found then fetch the fields and return the newly generated mapped values
   */
  public getMappedFieldsBySpaceUri(spaceUri: string): Observable<MappedEntityConfigField> {
    if (!spaceUri) {
      return;
    }
    console.assert(!!this.activeSpaceUri, 'activeSpaceUri should not be null');

    if (this.spaceMappedFields[this.activeSpaceUri]) {
      return of(this.spaceMappedFields[this.activeSpaceUri]);
    }

    return this.getFields(spaceUri).pipe(
      take(1),
      map((fields: Array<EntityConfigField>) =>
        fields.reduce((mappedFields: MappedEntityConfigField, field: EntityConfigField) => ({ ...mappedFields, [field.uri]: field }), {})
      )
    );
  }

  /**
   * Checks for field detail information in the cache and provides it.
   */
  public getField(fieldUri: string): Observable<EntityConfigField> {
    const activeSpaceUri: string = this.activeSpaceUri;
    console.assert(!!activeSpaceUri, 'activeSpaceUri should not be null');
    if (this.spaceMappedFields[activeSpaceUri] && this.spaceMappedFields[activeSpaceUri][fieldUri]) {
      return of(this.spaceMappedFields[activeSpaceUri][fieldUri]);
    }
    return this.getMappedFieldsBySpaceUri(activeSpaceUri).pipe(map((mappedField: MappedEntityConfigField) => mappedField[fieldUri]));
  }

  /**
   * Checks for field detail information in the cache and provides it.
   * If not found, fetches the fields and returns
   */
  public getFieldBySpaceUri(fieldUri: string, spaceUri: string): Observable<EntityConfigField> {
    return this.getMappedFieldsBySpaceUri(spaceUri).pipe(map((mappedFields) => mappedFields[fieldUri]));
  }

  /**
   * Provides the fieldVersion of the given fieldUri
   * @param fieldUri
   * @returns
   */
  public getFieldVersion(fieldUri: string): number {
    console.assert(!!this.activeSpaceUri, 'activeSpaceUri should not be null');
    return this.spaceMappedFields[this.activeSpaceUri][fieldUri].version;
  }

  /**
   * Check if the field has the itonics space uri
   * @param field
   * @returns
   */
  public hasItonicsSpaceUri(field: EntityConfigField): boolean {
    return ITONICS_URIS.includes(field.spaceUri);
  }

  /**
   * Update field cache
   * @param { EntityConfigField } saveField Field to update on cache
   * @param { string } spaceUri space on which the field needs to be updated
   */
  public async updateFieldCache(saveField: EntityConfigField, spaceUri: string): Promise<void> {
    const cachedFields: Array<EntityConfigField> = await lastValueFrom(this.spaceFields[spaceUri].pipe(take(1)));
    if (this.spaceFields[spaceUri]) {
      // Check if the field is already present on cache
      const fieldExist = cachedFields.some((field: EntityConfigField) => field.uri === saveField.uri);
      if (fieldExist) {
        // Update the cache with update field
        const updatedFields = cachedFields.map((field: EntityConfigField) => (field.uri === saveField.uri ? saveField : field));
        this.spaceFields[spaceUri].next(updatedFields);
      } else {
        // Add field to cache if not available on cache
        this.spaceFields[spaceUri].next([...cachedFields, saveField]);
      }
    }
    // Update space controls
    this.spaceMappedFields[spaceUri][saveField.uri] = saveField;
  }
}
